import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CheckoutDirectDebitMethod,
  DirectDebitForm,
  DirectDebitFormField,
} from '@checkout/util-models';
import {
  FormControlValidators,
  highlightInvalidControls,
} from '@domgen/dgx-fe-forms/shared';
import { TranslateService } from '@domgen/dgx-fe-i18n';
import { DirectDebitFormConfig } from '../../models/direct-debit-form/direct-debit-form-config.interface';
import { FeatureDirectDebitTranslationDictionary } from '../../models/i18n/feature-direct-debit-dictionary.interface';

@Injectable()
export class DirectDebitFormBuilder {
  private readonly accountDetailsControlKeys: DirectDebitFormField[] = [
    DirectDebitFormField.AccountNumber,
    DirectDebitFormField.IBAN,
    DirectDebitFormField.SortCode,
  ];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService<FeatureDirectDebitTranslationDictionary>
  ) {}

  buildDirectDebitForm(
    config: DirectDebitFormConfig
  ): FormGroup<DirectDebitForm> {
    return this.formBuilder.group(this.buildDirectDebitFormConfig(config));
  }

  private buildDirectDebitFormConfig(
    config: DirectDebitFormConfig
  ): DirectDebitForm {
    return {
      [DirectDebitFormField.AccountName]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_NAME_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.maximumLength(
            35,
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_NAME_ERROR_MAX_LENGTH',
              { maxLength: 35 }
            )
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_NAME_ERROR_ALPHABETICAL'
            )
          ),
        ])
      ),
      [DirectDebitFormField.AccountNumber]: new FormControl(
        {
          value: null,
          disabled:
            config.method !== CheckoutDirectDebitMethod.AccountNumberSortCode,
        },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_NUMBER_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.minimumLength(
            8,
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_NUMBER_ERROR_MIN_LENGTH',
              { minLength: 8 }
            )
          ),
          FormControlValidators.pattern(
            /^(\d){8}$/,
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_NUMBER_ERROR_PATTERN'
            )
          ),
        ])
      ),
      [DirectDebitFormField.IBAN]: new FormControl(
        {
          value: null,
          disabled: config.method !== CheckoutDirectDebitMethod.IBAN,
        },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_IBAN_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.IBAN(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_IBAN_ERROR_PATTERN'
            )
          ),
        ])
      ),
      [DirectDebitFormField.PaymentToken]: new FormControl(null),
      [DirectDebitFormField.SortCode]: new FormControl(
        {
          value: null,
          disabled:
            config.method !== CheckoutDirectDebitMethod.AccountNumberSortCode,
        },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_SORT_CODE_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.minimumLength(
            6,
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_SORT_CODE_ERROR_MIN_LENGTH',
              { minLength: 6 }
            )
          ),
          FormControlValidators.pattern(
            /^(\d){6}$/,
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_SORT_CODE_ERROR_PATTERN'
            )
          ),
        ])
      ),
      [DirectDebitFormField.PaymentDate]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_PAYMENT_DATE_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [DirectDebitFormField.AccountHolderConfirmation]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.requiredTrue(
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_ACCOUNT_HOLDER_CONFIRMATION_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [DirectDebitFormField.TermsAndConditions]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.requiredTrueConditionally(
            Boolean(config.termsAndConditionsFeatureEnabled),
            this.translateService.translate(
              'DIRECT_DEBIT_FORM_TERMS_AND_CONDITIONS_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [DirectDebitFormField.PaperlessPreference]: new FormControl(null),
    };
  }

  setAccountDetailsErrors(form: FormGroup<DirectDebitForm>): void {
    form.patchValue(
      this.accountDetailsControlKeys.reduce(
        (
          formValue: Partial<Record<DirectDebitFormField, null>>,
          controlKey: DirectDebitFormField
        ) => ({
          ...formValue,
          [controlKey]: '',
        }),
        {}
      )
    );

    this.accountDetailsControlKeys.forEach((controlKey) => {
      form.get(controlKey)?.setErrors(
        {
          invalidAccountNumberSortCode: this.translateService.translate(
            'DIRECT_DEBIT_INVALID_ACCOUNT_DETAILS_ERROR'
          ),
        },
        { emitEvent: true }
      );
    });

    highlightInvalidControls(form);
  }
}
